/**
 * --- GOOGLE SHEETS "DATABASE" SERVICE ---
 * * SETUP INSTRUCTIONS (For when you are ready to go live):
 * 1. Create a Google Form with: Name (Short Answer), Phone (Short Answer), Email (Short Answer), Clinical Data (Paragraph).
 * 2. Get the entry IDs for these fields (inspect element on the form view -> entry.123456).
 * 3. Link the Form to a Google Sheet.
 * 4. File -> Share -> Publish to Web -> Select "Sheet1" and "CSV".
 * 5. Paste the Form URL and CSV URL below.
 */

// CONFIGURATION - loaded from environment variables with safe fallbacks
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === "true";
const GOOGLE_FORM_ACTION_URL =
    process.env.REACT_APP_GOOGLE_FORM_ACTION_URL ||
    "https://docs.google.com/forms/d/e/1FAIpQLSdhELlyBxTwznsulw-CouJDa6587Wa3eOjTGoTN_v4WTN9GhA/formResponse";
const GOOGLE_SHEET_CSV_URL =
    process.env.REACT_APP_GOOGLE_SHEET_CSV_URL ||
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vSKBLM-Tv5bmFp7IGaBoRGg3sOLyd_tFxHxu0qeXRMS9HKObLe5b_An0oZ0exarU-gJy0sm3PFsT0kQ/pub?gid=911105283&single=true&output=csv";

// Map your Form Entry IDs here
const FORM_FIELDS = {
    name: process.env.REACT_APP_FORM_FIELD_NAME || "entry.2005620554",
    phone: process.env.REACT_APP_FORM_FIELD_PHONE || "entry.1166974658",
    email: process.env.REACT_APP_FORM_FIELD_EMAIL || "entry.1045781291",
    clinicalData: process.env.REACT_APP_FORM_FIELD_CLINICAL_DATA || "entry.1192316146" // The hidden field for the full JSON report
};

// --- MOCK DATA (For Development - Only used if USE_MOCK_DATA is true or fetch fails) ---
const MOCK_REPORTS = [
  {
    id: "sheet-row-1",
    name: "John Doe",
    phone: "555-0123",
    email: "john.doe@example.com",
    status: "new",
    createdAt: { seconds: Date.now() / 1000 },
    // This replicates the data structure stored in the store
    painLocation: "Knee",
    preciseLocation: { id: "G", name: "Tibial Tuberosity" },
    age: "19-40",
    gender: "Male",
    painDuration: "0-6 weeks",
    onset: "sudden",
    onsetDetail: "During exercise",
    painIntensity: 7,
    painType: "Sharp",
    aggravatingFactor: ["Running"],
    relievingFactor: ["Rest"],
    hasSwelling: true,
    worsensWithPressure: true,
    medicalConditions: ["None"]
  },
  {
    id: "sheet-row-2",
    name: "Jane Smith",
    phone: "555-0987",
    email: "jane.smith@example.com",
    status: "reviewed",
    createdAt: { seconds: (Date.now() - 86400000) / 1000 },
    painLocation: "Shoulder",
    preciseLocation: { id: "A", name: "Supraspinatus" },
    age: "41-59",
    gender: "Female",
    painDuration: "12+ weeks",
    painIntensity: 4,
    painType: "Dull",
    hasSwelling: false,
    worsensWithPressure: false,
    medicalConditions: ["Diabetes"]
  }
];

/**
 * Saves patient data to Google Forms
 */
export const savePatientReport = async (fullData) => {
    if (USE_MOCK_DATA) {
        console.log("--- MOCK GOOGLE FORM SUBMIT ---", fullData);
        return new Promise(resolve => setTimeout(resolve, 1000));
    }

    // UPDATED: Using URLSearchParams instead of FormData
    // This sends data as 'application/x-www-form-urlencoded' which is often more reliable
    // for Google Forms than multipart/form-data, and helps avoid some CORS/Boundary issues.
    const params = new URLSearchParams();
    params.append(FORM_FIELDS.name, fullData.name);
    params.append(FORM_FIELDS.phone, fullData.phone);
    params.append(FORM_FIELDS.email, fullData.email);
    
    // We stringify the entire clinical object to store it in one cell
    const clinicalJson = JSON.stringify(fullData); 
    params.append(FORM_FIELDS.clinicalData, clinicalJson);

    try {
        await fetch(GOOGLE_FORM_ACTION_URL, {
            method: "POST",
            mode: "no-cors", // Required for Google Forms to allow the request without CORS headers from Google
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: params.toString()
        });
        
        // Note: With 'no-cors', we cannot read the response status or body.
        // It returns an opaque response. If the network request fails, it throws.
        // If you still see 401 in console, check Form Settings -> "Restrict to users in Organization"
        return true;
    } catch (error) {
        console.error("Form Submit Error:", error);
        throw error;
    }
};

/**
 * Fetches reports from the Google Sheet (CSV)
 */
export const getAllReports = async () => {
    if (USE_MOCK_DATA) {
        return new Promise(resolve => setTimeout(() => resolve(MOCK_REPORTS), 800));
    }

    try {
        const response = await fetch(GOOGLE_SHEET_CSV_URL);
        const text = await response.text();
        const reports = parseCSV(text);

        // --- APPLY LOCAL STATUS OVERRIDES ---
        // Since we can't write to the Google Sheet via simple fetch, we use LocalStorage 
        // to persist the "Reviewed" status locally for the admin.
        const storedStatuses = JSON.parse(localStorage.getItem('report_statuses') || '{}');
        
        return reports.map(report => ({
            ...report,
            // If we have a stored status for this ID (e.g. 'reviewed'), use it. 
            // Otherwise fall back to the default 'new'.
            status: storedStatuses[report.id] || report.status
        }));

    } catch (error) {
        console.error("Sheet Fetch Error:", error);
        // Fallback to mock data if sheet is not published correctly or network fails
        return MOCK_REPORTS; 
    }
};

// Helper to convert CSV text to Objects
const parseCSV = (csvText) => {
    // 1. ROBUST CSV PARSING (Handles newlines inside quotes)
    const rows = [];
    let currentRow = [];
    let currentVal = '';
    let insideQuote = false;
    
    // Normalize newlines to \n
    csvText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    for (let i = 0; i < csvText.length; i++) {
        const char = csvText[i];
        const nextChar = csvText[i + 1];

        if (char === '"') {
            if (insideQuote && nextChar === '"') {
                currentVal += '"';
                i++; // Skip the escaped quote
            } else {
                insideQuote = !insideQuote;
            }
        } else if (char === ',' && !insideQuote) {
            currentRow.push(currentVal);
            currentVal = '';
        } else if (char === '\n' && !insideQuote) {
            currentRow.push(currentVal);
            if (currentRow.length > 0 || currentVal) {
                rows.push(currentRow);
            }
            currentRow = [];
            currentVal = '';
        } else {
            currentVal += char;
        }
    }
    // Push the final row if exists
    if (currentRow.length > 0 || currentVal) {
        currentRow.push(currentVal);
        rows.push(currentRow);
    }

    if (rows.length === 0) return [];

    // 2. DYNAMIC COLUMN MAPPING
    // Detect column indices from the header row (first row)
    const headers = rows[0].map(h => h.toLowerCase().trim());
    
    const getIndex = (keywords) => headers.findIndex(h => keywords.some(k => h.includes(k)));
    
    // Heuristics for finding columns
    const nameIdx = getIndex(['name', 'full name', 'patient']);
    const phoneIdx = getIndex(['phone', 'mobile', 'contact']);
    const emailIdx = getIndex(['email', 'e-mail']);
    const jsonIdx = getIndex(['clinical', 'data', 'json', 'report']);
    const timeIdx = getIndex(['timestamp', 'date', 'time']);

    // Helper to clean a value safely
    const clean = (val) => val ? val.trim() : '';

    return rows.slice(1).map((values, index) => {
        // Fallback to default indices if headers aren't found (0=Time, 1=Name, 2=Phone, 3=Email, 4=JSON)
        // Checks bounds to avoid undefined errors
        const getVal = (idx, fallbackIdx) => {
            const i = idx !== -1 ? idx : fallbackIdx;
            return values[i] !== undefined ? clean(values[i]) : '';
        };

        const timestampStr = getVal(timeIdx, 0);
        const name = getVal(nameIdx, 1);
        const phone = getVal(phoneIdx, 2);
        const email = getVal(emailIdx, 3);
        
        // Logical "Clinical Data" extraction
        let clinicalData = {};
        
        // STRATEGY: 
        // 1. Try column mapped by header 'clinical'/'data'
        // 2. If valid JSON not found there, scan ALL columns for a JSON object with expected keys.
        
        let jsonRaw = '';
        if (jsonIdx !== -1 && values[jsonIdx]) {
            jsonRaw = values[jsonIdx];
        } else {
            // Default Fallback
            jsonRaw = values[4] || '';
        }

        const parseClinical = (str) => {
            try {
                const parsed = JSON.parse(str);
                if (parsed && typeof parsed === 'object') return parsed;
            } catch(e) { return null; }
            return null;
        };

        let parsedData = parseClinical(jsonRaw);

        // If specific column failed, scan the row for ANY valid JSON looking like our data
        if (!parsedData) {
            for (const val of values) {
                const candidate = parseClinical(val);
                // Check for characteristic keys of our report
                if (candidate && (candidate.painLocation || candidate.painIntensity || candidate.medicalConditions)) {
                    parsedData = candidate;
                    break;
                }
            }
        }

        if (parsedData) {
            clinicalData = parsedData;
        } else {
            console.warn(`Row ${index}: Could not find valid Clinical JSON`);
        }
        
        // Generate a stable ID based on timestamp and patient contact to avoid shifting index issues
        const timeEpoch = new Date(timestampStr).getTime();
        const contactSlug = (email || phone || name || `idx-${index}`).replace(/\W/g, '');
        const reportId = timeEpoch ? `rep_${timeEpoch}_${contactSlug}` : `row-${index}`;

        return {
            id: reportId,
            status: 'new', 
            // Parsing Date: 
            createdAt: { seconds: (timeEpoch / 1000) || (Date.now() / 1000) },
            name: name,
            phone: phone,
            email: email,
            ...clinicalData 
        };
    });
};

export const updateReportStatus = async (id, status) => {
    try {
        // Persist status change to LocalStorage to mimic database update
        const storedStatuses = JSON.parse(localStorage.getItem('report_statuses') || '{}');
        storedStatuses[id] = status;
        localStorage.setItem('report_statuses', JSON.stringify(storedStatuses));
        
        console.log(`Status updated to ${status} (Local Cache)`);
        return true;
    } catch (e) {
        console.error("Failed to update status", e);
        return false;
    }
};
